import { useCallback } from 'react';

import { useConfirm } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';
import { ApiError } from '@/lib/api/errors';

import { useCommandStore, useDeviceCommand } from './command-store';
import { expectedWaitLabel } from './format';
import { useCancelCommand, useIssueCommand } from './hooks';
import type { CommandState, CommandType, Device } from './types';

export type CommandLifecycle = {
  /** A command is in flight (Queued/Delivered) for this device. */
  pending: boolean;
  /** Latest lifecycle state of the in-flight command, or null. */
  state: CommandState | null;
  /** The in-flight command type (drives the "Turn On/Off Queued" banner copy). */
  type: CommandType | null;
  /** Whether the in-flight command can still be pulled back (only while Queued). */
  cancelable: boolean;
  /** Issue a command. Power on/off first asks the grower to confirm (AC5). */
  run: (type: CommandType) => void;
  /** Cancel the queued command (no-op once it has been Delivered). */
  cancel: () => void;
};

const VERB: Record<CommandType, string> = {
  turn_on: 'Turn On',
  turn_off: 'Turn Off',
  stop_timer: 'Stop Timer',
  schedule: 'Schedule',
};

/** Power commands prompt for confirmation; stop-timer/schedule are confirmed by their drawers. */
function needsConfirm(type: CommandType): type is 'turn_on' | 'turn_off' {
  return type === 'turn_on' || type === 'turn_off';
}

/**
 * Issue a remote command and track its lifecycle (story 101849):
 * Queued → Delivered → Executed | Failed. The transitions, resolve toast, and cache
 * refresh are owned by the app-level `<CommandAckBridge>`; this hook only issues, reads
 * the shared in-flight state, and cancels. Rate-limit (429) is surfaced without entering
 * the pending state.
 */
export function useCommandLifecycle(device: Device): CommandLifecycle {
  const toast = useToast();
  const { confirm } = useConfirm();
  const issue = useIssueCommand(device.id);
  const cancelCmd = useCancelCommand(device.id);
  const setCommand = useCommandStore((s) => s.setCommand);
  const clearCommand = useCommandStore((s) => s.clearCommand);
  const command = useDeviceCommand(device.id);

  const run = useCallback(
    (type: CommandType) => {
      if (command) return;

      const dispatch = () => {
        issue.mutate(
          { type },
          {
            onSuccess: (cmd) => setCommand(cmd),
            onError: (err) => {
              const message =
                err instanceof ApiError && err.status === 429
                  ? 'Too many commands. Please wait a minute and try again.'
                  : 'Could not send the command. Please try again.';
              toast.error({ title: 'Command not sent', description: message });
            },
          },
        );
      };

      if (!needsConfirm(type)) {
        dispatch();
        return;
      }

      const action = type === 'turn_on' ? 'Turn on' : 'Turn off';
      const verb = type === 'turn_on' ? 'turn on' : 'turn off';
      void confirm({
        title: `${action} ${device.name}?`,
        subtitle: `The pump will ${verb} at its next check-in — ${expectedWaitLabel(device)}.`,
        confirmText: action,
        cancelText: 'Cancel',
        onConfirm: dispatch,
      });
    },
    [command, confirm, device, issue, setCommand, toast],
  );

  const cancel = useCallback(() => {
    if (!command || command.state !== 'queued') return;
    const verb = VERB[command.type];
    cancelCmd.mutate(command.id, {
      onSuccess: () => {
        clearCommand(device.id);
        toast.info({ title: `${verb} cancelled` });
      },
      onError: () => toast.error({ title: 'Could not cancel', description: 'Please try again.' }),
    });
  }, [cancelCmd, clearCommand, command, device.id, toast]);

  return {
    pending: command != null,
    state: command?.state ?? null,
    type: command?.type ?? null,
    cancelable: command?.state === 'queued',
    run,
    cancel,
  };
}
